import { useEffect, useState } from "react"

import GeneralBg from "@/assets/img/profile/advisorQuiz/general_background.webp"
import CounterImage from "@/assets/img/profile/advisorQuiz/counter_image.webp"
import CounterEllipse from "@/assets/img/profile/advisorQuiz/counter_ellipse_186.webp"

import { LOADING_LABELS } from "../../config/quiz-steps"

const TOTAL_MS = 6000
const SEGMENT_MS = TOTAL_MS / LOADING_LABELS.length

interface QuizLoadingProps {
  onComplete: () => void
}

/** Six-second "analyzing your answers" screen. Ported from AdvisorsQuiz Step7 Loading. */
export function QuizLoading({ onComplete }: QuizLoadingProps) {
  const [progress, setProgress] = useState(0)

  useEffect(() => {
    let raf = 0
    let start: number | null = null
    let done = false
    let completeTimer = 0

    const tick = (ts: number) => {
      if (start === null) start = ts
      const elapsed = ts - start
      setProgress(Math.min(100, (elapsed / TOTAL_MS) * 100))
      if (elapsed < TOTAL_MS) {
        raf = window.requestAnimationFrame(tick)
      } else if (!done) {
        done = true
        completeTimer = window.setTimeout(onComplete, 300)
      }
    }

    raf = window.requestAnimationFrame(tick)
    return () => {
      window.cancelAnimationFrame(raf)
      window.clearTimeout(completeTimer)
    }
  }, [onComplete])

  const elapsedMs = (progress / 100) * TOTAL_MS

  return (
    <div className="relative flex min-h-[100dvh] flex-col bg-dark-base">
      <img
        src={GeneralBg}
        alt=""
        className="pointer-events-none absolute inset-x-0 top-0 h-60 w-full object-cover"
      />

      <div className="relative z-[1] mx-auto flex w-full max-w-[500px] flex-col items-center gap-6 px-4 pb-6 pt-8">
        <div className="relative size-[220px]">
          <img
            src={CounterEllipse}
            alt=""
            className="pointer-events-none absolute inset-0 size-full object-contain"
          />
          <div className="absolute left-1/2 top-1/2 flex size-[186px] -translate-x-1/2 -translate-y-1/2 items-center justify-center">
            <img
              src={CounterImage}
              alt=""
              className="pointer-events-none absolute inset-0 size-full object-cover"
            />
            <span className="relative font-display text-[40px] font-bold text-white">
              {Math.round(progress)}%
            </span>
          </div>
        </div>

        <h2 className="text-center font-display text-2xl font-medium text-purple-light">
          Finding your perfect guides
        </h2>

        <div className="flex w-full flex-col gap-4">
          {LOADING_LABELS.map((label, i) => {
            const segment = Math.max(0, Math.min(SEGMENT_MS, elapsedMs - i * SEGMENT_MS))
            const fill = (segment / SEGMENT_MS) * 100
            return (
              <div key={label} className="flex flex-col gap-2">
                <p className="font-body text-sm text-purple-light/70">{label}</p>
                <div className="h-1 w-full overflow-hidden rounded-full bg-purple-light/20">
                  <div className="h-full bg-main-purple" style={{ width: `${fill}%` }} />
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
